<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" :schema="schema" :state="state" :validate-on="['submit']" class="h-full" @submit="save">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            {{ item ? 'Edit Item' : 'Add Item' }}
                        </h3>
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="kind" label="Kind" required>
                        <USelectMenu v-model="state.kind" :options="kinds" value-attribute="value" option-attribute="label" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="name" label="Name" required>
                        <UInput v-model="state.name" type="text" size="xl" placeholder="Adrenaline 1 mg/ml" />
                    </UFormGroup>
                    <UFormGroup name="specification" label="Specification" help="Strength or size, e.g. 1 mg, 0.6 x 25, 7.5.">
                        <UInput v-model="state.specification" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="unit" label="Unit" help="Piece, ampoule, vial, tablet, bag, mL." required>
                        <UInput v-model="state.unit" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup v-if="item" name="is_archived" label="Status">
                        <div class="relative border border-gray-200 rounded-md p-4 mt-2">
                            <div class="flex items-center justify-between">
                                <p class="text-sm font-medium">Archived</p>
                                <UToggle v-model="state.is_archived" size="xs" color="primary" />
                            </div>
                        </div>
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton size="lg" color="white" @click="dismiss()">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" color="black" :loading="saving">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { InventoryItemKind, type InventoryItem } from '~/types/fieldPost'

const props = defineProps<{
    open: boolean
    item?: InventoryItem
}>()

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const client = useSupabaseClient()
const toast = useToast()

const kinds = [
    { value: InventoryItemKind.consumable, label: 'Consumable' },
    { value: InventoryItemKind.medication, label: 'Medication' },
]

const form = ref()
const saving = ref(false)

const schema = z.object({
    kind: z.nativeEnum(InventoryItemKind, { errorMap: () => ({ message: 'Kind is required.' }) }),
    name: z.string().trim().min(1, 'Name is required.'),
    unit: z.string().trim().min(1, 'Unit is required.'),
})

const state = reactive({
    kind: InventoryItemKind.consumable,
    name: '',
    specification: '',
    unit: 'piece',
    is_archived: false,
})

watch(() => props.open, (open) => {
    if (!open) return
    state.kind = props.item?.kind ?? InventoryItemKind.consumable
    state.name = props.item?.name ?? ''
    state.specification = props.item?.specification ?? ''
    state.unit = props.item?.unit ?? 'piece'
    state.is_archived = props.item?.is_archived ?? false
})

async function save() {
    const payload = {
        kind: state.kind,
        name: state.name.trim(),
        specification: state.specification.trim() || null,
        unit: state.unit.trim(),
        is_archived: state.is_archived,
    }

    saving.value = true
    const { error } = props.item
        ? await client.from('inventory_items').update(payload as never).eq('id', props.item.id)
        : await client.from('inventory_items').insert(payload as never)
    saving.value = false

    if (error) {
        toast.add({ title: 'Could not save the item.', description: error.message, color: 'red', icon: 'i-ph-x-circle-duotone' })
        return
    }

    toast.add({ title: 'Item saved.', color: 'green', icon: 'i-ph-check-circle-duotone' })
    emit('reload')
    dismiss()
}

function dismiss() {
    emit('update:open', false)
}
</script>
